import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { SplitSupplyNoticeErrorReason } from '@ebalance/proto/bffuser/v1/split_supply_plan_model_pb'
import type { PreviewOwnSplitSupplyNoticeImportResponse } from '@ebalance/proto/bffuser/v1/split_supply_plan_service_pb'
import { useTranslation } from 'react-i18next'

import { Flex, TertiaryButton } from '@enechain/ecloud-designsystem'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { type CivilDate, compareCivilDate } from '~/models/civilDate'
import { NoticeImportDialog } from '~/pages/SplitSupplyPlan/components/NoticeImportDialog'
import { OwnImportConfirmation } from '~/pages/SplitSupplyPlan/components/OwnImportConfirmation'
import {
  useDownloadOwnNoticeCsvTemplate,
  useImportOwnSplitSupplyNotices,
  usePreviewOwnNoticeImport,
  useUploadOwnNoticeFile,
} from '~/pages/SplitSupplyPlan/hooks/useOwnNoticeImport'
import { OWN_IMPORT_KEY_PREFIX } from '~/pages/SplitSupplyPlan/models/ownImport'
import {
  SECTION_OF_TAB,
  type SectionTab,
  earliestEditableTarget,
} from '~/pages/SplitSupplyPlan/models/period'

type Props = {
  isOpen: boolean
  onClose: () => void
  /** 開いている断面 (ファイルはこの断面のものを選ぶ)。 */
  tab: SectionTab
  /** 一覧の今の対象日 (翌日は日、 月間は月の 1 日)。 CSV テンプレートの期間になる。 */
  target: CivilDate
  /** 一覧の今のエリアの絞り込み。 無ければ全エリアの地点がテンプレートの列になる。 */
  area: AreaCode | undefined
  today: CivilDate
}

/** 内容が拒まれた (またはファイルの受け取りが切れた) ので、 選んだファイルでは続けられない reason。 */
const RESTART_REASONS: ReadonlySet<SplitSupplyNoticeErrorReason> = new Set([
  SplitSupplyNoticeErrorReason.OWN_NOTICE_IMPORT_UPLOAD_EXPIRED,
  SplitSupplyNoticeErrorReason.OWN_NOTICE_IMPORT_INVALID,
  SplitSupplyNoticeErrorReason.OWN_NOTICE_IMPORT_NOTHING,
  SplitSupplyNoticeErrorReason.OWN_NOTICE_IMPORT_TOO_LARGE,
])

/**
 * 自社の通告値の取込のダイアログ (流れは `NoticeImportDialog`)。 取り込む内容は地点ごとの新規作成 /
 * ファイルにある日の上書きで、 確認画面は `OwnImportConfirmation`。 1 段目に取込用の CSV テンプレート
 * (自社が通告値を出す地点の列) のダウンロードを置く。 取り込めない期間 (過ぎた日) はテンプレートを
 * 落とせず、 理由を hover に出す。
 */
export const ImportOwnNoticesDialog = ({
  isOpen,
  onClose,
  tab,
  target,
  area,
  today,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const uploadFile = useUploadOwnNoticeFile()
  const { mutateAsync: previewImport, isPending: isPreviewing } =
    usePreviewOwnNoticeImport()
  const { mutateAsync: runImport, isPending: isImporting } =
    useImportOwnSplitSupplyNotices()
  const { download } = useDownloadOwnNoticeCsvTemplate()
  const isClosed =
    compareCivilDate(target, earliestEditableTarget(tab, today)) < 0

  return (
    <NoticeImportDialog<PreviewOwnSplitSupplyNoticeImportResponse>
      isOpen={isOpen}
      onClose={onClose}
      side={{
        prefix: OWN_IMPORT_KEY_PREFIX,
        uploadFile,
        preview: previewImport,
        runImport: async (input) => {
          const res = await runImport(input)
          return {
            title: t(`${OWN_IMPORT_KEY_PREFIX}.success`, {
              count: res.createdPointCount + res.overwrittenPointCount,
              created: res.createdPointCount,
              overwritten: res.overwrittenPointCount,
            }),
          }
        },
        isPreviewing,
        isImporting,
        restartReasons: RESTART_REASONS,
        changedReason: SplitSupplyNoticeErrorReason.OWN_NOTICE_IMPORT_CHANGED,
        renderConfirmation: ({ result, onRemoveBadFiles }) => (
          <OwnImportConfirmation
            onRemoveBadFiles={onRemoveBadFiles}
            result={result}
          />
        ),
        selectExtra: (
          <Flex>
            <ButtonTooltip
              content={
                isClosed
                  ? t(`${OWN_IMPORT_KEY_PREFIX}.template.closedHint`)
                  : undefined
              }
              focusable
            >
              <TertiaryButton
                disabled={isClosed}
                onClick={() => {
                  void download({
                    section: SECTION_OF_TAB[tab],
                    targetDate: target,
                    areaCode: area,
                  })
                }}
                size="md"
              >
                {t(`${OWN_IMPORT_KEY_PREFIX}.template.button`)}
              </TertiaryButton>
            </ButtonTooltip>
          </Flex>
        ),
      }}
      tab={tab}
    />
  )
}
